import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

/**
 * Basil dark mode (Phase 1, deploy 3 of 3).
 *
 * One switch, three consumers:
 *  - <html data-theme="dark"> re-points every Tier-2 token in tokens.css
 *  - GlobalMuiOverrides reads the mode and swaps the MUI palette mirrors
 *  - color-scheme flips so native controls (scrollbars, inputs) follow
 *
 * Preference persists in localStorage; first visit follows the OS.
 */

type ColorMode = 'light' | 'dark';

const STORAGE_KEY = 'sog-color-mode';

/** Kill switch: dark mode disabled in production by owner decision
 * (2026-08-05 — "does not look good"). Infra stays; flip to re-enable. */
const DARK_ENABLED = false;

interface ColorModeContextValue {
  mode: ColorMode;
  toggle: () => void;
  setMode: (m: ColorMode) => void;
}

const ColorModeContext = createContext<ColorModeContextValue | null>(null);

function initialMode(): ColorMode {
  if (!DARK_ENABLED) return 'light';
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') return stored;
  } catch { /* private browsing */ }
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export const ColorModeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [mode, setMode] = useState<ColorMode>(initialMode);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', mode);
    try { localStorage.setItem(STORAGE_KEY, mode); } catch { /* ignore */ }
  }, [mode]);

  // Follow OS changes only while the user hasn't chosen explicitly this session
  useEffect(() => {
    const mq = window.matchMedia?.('(prefers-color-scheme: dark)');
    if (!mq) return;
    const onChange = (e: MediaQueryListEvent) => {
      try {
        if (!localStorage.getItem(STORAGE_KEY)) setMode(e.matches ? 'dark' : 'light');
      } catch { /* ignore */ }
    };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const toggle = useCallback(() => { if (DARK_ENABLED) setMode((m) => (m === 'light' ? 'dark' : 'light')); }, []);
  const value = useMemo(() => ({ mode, toggle, setMode }), [mode, toggle]);

  return <ColorModeContext.Provider value={value}>{children}</ColorModeContext.Provider>;
};

export function useColorMode(): ColorModeContextValue {
  const ctx = useContext(ColorModeContext);
  if (!ctx) throw new Error('useColorMode must be used within ColorModeProvider');
  return ctx;
}

/** Same, but tolerant of missing provider (unit tests, isolated renders). */
export function useColorModeSafe(): ColorModeContextValue {
  const ctx = useContext(ColorModeContext);
  return ctx ?? { mode: 'light', toggle: () => {}, setMode: () => {} };
}
